import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type Hospitalisation from "parog-web/models/hospitalisation";
import type MedicationDispense from "parog-web/models/medication-dispense";
import type Patient from "parog-web/models/patient";
import type Performer from "parog-web/models/performer";
import type Prescriptor from "parog-web/models/prescriptor";
import tWithDefault from "parog-web/utils/t-with-default";
import JournalEvent from "./refunded-journal-event";

//---- Automatically generated, don't update this part ----

type RiskStringEnum =
  | "MALADIE"
  | "MATERNITE"
  | "ACCIDENT"
  | "TRAVAIL"
  | "PREVENTION";

type ExemptionStringEnum = "ALD";

/**
 * Describe "OJ" (pharmacie, fournitures) reimbursement.
 * Property descriptions are sometimes copy/pasted from the DMP specification.
 *
 * @see sel-mp-005_hr-integre-lps_v-1.9.pdf for details.
 *
 * The DMP feed provides an array of drugs and devices for each Execution
 */
export default class DrugsAndDevicesRefundedJournalEvent extends JournalEvent {
  static override type = "drugs-and-devices-refunded-journal-event";

  @attr("hospitalisation") declare hospitalisation: Hospitalisation;

  /**
   * Nature du risque au titre duquel la prestation est prise en charge.
   * Valeurs possibles : «MALADIE», «MATERNITE», «ACCIDENT», «TRAVAIL»,
   * «PREVENTION»
   */
  @attr("string") declare risk: RiskStringEnum;

  /**
   * Valeur = «ALD» si présente
   */
  @attr("string") declare exemption: ExemptionStringEnum;

  /**
   * DMP: PrestationFacturee::DetailDePrestation
   */
  @attr() declare prestations: any[];

  @attr("prescriptor") declare prescriptor: Prescriptor;

  @attr("performer") declare performer: Performer;

  static override validations(intl: IntlService) {
    return {
      reimbursementsRetrievedJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.drugs-and-devices-refunded-journal-event.reimbursements-retrieved-journal-event"
        )
      }),
      prestations: validatePresence({
        presence: true,
        description: intl.t(
          "validations.drugs-and-devices-refunded-journal-event.prestations"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.drugs-and-devices-refunded-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      risk: validateInclusion({
        list: ["MALADIE", "MATERNITE", "ACCIDENT", "TRAVAIL", "PREVENTION"],
        allowBlank: true,
        description: intl.t(
          "validations.drugs-and-devices-refunded-journal-event.risk"
        )
      }),
      exemption: validateInclusion({
        list: ["ALD"],
        allowBlank: true,
        description: intl.t(
          "validations.drugs-and-devices-refunded-journal-event.exemption"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "drugsAndDevicesRefundedJournalEvents"
  })
  declare patient: Patient;

  @hasMany("medication-dispense", {
    async: false,
    inverse: "drugsAndDevicesRefundedJournalEvent"
  })
  declare medicationDispenses: MedicationDispense[];
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "drugs-and-devices-refunded-journal-event": DrugsAndDevicesRefundedJournalEvent;
  }
}
